import { getStoredLegacyToken } from './legacyAuthService';

type QueryValue = string | number | boolean | undefined | null;

function buildQuery(query?: Record<string, QueryValue>): string {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

async function request<T>(path: string): Promise<T> {
  const token = getStoredLegacyToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }

  const response = await fetch(path, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `API error (${response.status})`);
  }

  return (await response.json()) as T;
}

export type MobileAnalyticsTimelinePoint = {
  date: string;
  label: string;
  events: number;
  sessions: number;
  users: number;
  anonymousEvents: number;
};

export type MobileAnalyticsSummary = {
  totalEvents: number;
  uniqueSessions: number;
  uniqueUsers: number;
  anonymousEvents: number;
  anonymousSessions: number;
  eventTypeCounts: Record<string, number>;
  timeline: MobileAnalyticsTimelinePoint[];
  topExperienceUsage: Array<{ experience: string; total: number }>;
  latestEvents: Array<{
    id: string;
    eventType: string;
    sessionId: string;
    idExperience: number | null;
    experienceName: string | null;
    userId: string | null;
    isAnonymous: boolean;
    platform: string;
    createdAtIso: string | null;
  }>;
  meta: {
    days: number;
    source: string;
    fetched: number;
    afterFilters: number;
    generatedAt: string;
    idExperience: number | null;
  };
};

export type UserPersonaRow = {
  id: string;
  gender: string | null;
  preferredName: string | null;
  mainInterest: string | null;
  timeAvailable: string | null;
  visitStyle: string | null;
  companionType: string | null;
  knowledgeLevel: string | null;
  accessibilityNeed: string | null;
  experienceGoal: string | null;
  updatedAt: string | null;
};

export type MobileAnalyticsEventRow = {
  id: string;
  eventType: string;
  sessionId: string;
  idExperience: number | null;
  experienceName: string | null;
  userId: string | null;
  isAnonymous: boolean;
  platform: string;
  appVersion?: string | null;
  createdAtIso: string | null;
  payload?: Record<string, unknown>;
};

export type MobileAnalyticsEventsResponse = {
  events: MobileAnalyticsEventRow[];
  aggregates: {
    totalEvents: number;
    uniqueSessions: number;
    uniqueUsers: number;
    anonymousEvents: number;
    eventTypeCounts: Record<string, number>;
    timeline: MobileAnalyticsTimelinePoint[];
    topExperienceUsage: Array<{ experience: string; total: number }>;
    profiling?: {
      mainInterest: Record<string, number>;
      timeAvailable: Record<string, number>;
      visitStyle: Record<string, number>;
      companionType: Record<string, number>;
      knowledgeLevel: Record<string, number>;
      accessibilityNeed: Record<string, number>;
      experienceGoal: Record<string, number>;
      gender: Record<string, number>;
    };
  };
  pagination: {
    limit: number;
    nextCursor: string | null;
    hasMore: boolean;
  };
  userPersonas?: UserPersonaRow[];
  meta: {
    days: number;
    fromDate: string | null;
    toDate: string | null;
    source: string;
    generatedAt: string;
    idExperience: number | null;
    eventType: string | null;
    queryText: string | null;
  };
};

type MobileAnalyticsSummaryResponse = {
  success: boolean;
  summary: MobileAnalyticsSummary;
};

type MobileAnalyticsEventsApiResponse = {
  success: boolean;
  data: MobileAnalyticsEventsResponse;
};

export async function getMobileAnalyticsSummary(params?: {
  days?: number;
  idExperience?: number;
  limit?: number;
}): Promise<MobileAnalyticsSummary> {
  const query = buildQuery({
    days: params?.days ?? 30,
    idExperience: params?.idExperience,
    limit: params?.limit ?? 5000,
  });

  const payload = await request<MobileAnalyticsSummaryResponse>(`/api/mobile-analytics/summary${query}`);
  return payload.summary;
}

export async function getMobileAnalyticsEvents(params?: {
  days?: number;
  fromDate?: string;
  toDate?: string;
  idExperience?: number;
  eventType?: string;
  queryText?: string;
  cursor?: string;
  limit?: number;
}): Promise<MobileAnalyticsEventsResponse> {
  const query = buildQuery({
    days: params?.days ?? 30,
    fromDate: params?.fromDate,
    toDate: params?.toDate,
    idExperience: params?.idExperience,
    eventType: params?.eventType,
    queryText: params?.queryText,
    cursor: params?.cursor,
    limit: params?.limit ?? 200,
  });

  const payload = await request<MobileAnalyticsEventsApiResponse>(`/api/mobile-analytics/events${query}`);
  return payload.data;
}
